import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../api/client';
import toast from 'react-hot-toast';
import {
  XMarkIcon, ArrowRightOnRectangleIcon, ArrowLeftOnRectangleIcon,
  DocumentArrowUpIcon, ChatBubbleLeftRightIcon, UserIcon,
  BuildingOffice2Icon, FolderIcon, ClockIcon, KeyIcon,
} from '@heroicons/react/24/outline';

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ACTION_META = {
  login:               { icon: ArrowRightOnRectangleIcon, color: 'text-green-600 bg-green-50',   label: 'Logged in' },
  logout:              { icon: ArrowLeftOnRectangleIcon,  color: 'text-slate-500 bg-slate-100',  label: 'Logged out' },
  document_upload:     { icon: DocumentArrowUpIcon,       color: 'text-blue-600 bg-blue-50',     label: 'Uploaded a document' },
  ai_chat:             { icon: ChatBubbleLeftRightIcon,   color: 'text-violet-600 bg-violet-50', label: 'AI chat' },
  user_created:        { icon: UserIcon,                  color: 'text-indigo-600 bg-indigo-50', label: 'Created a user' },
  user_updated:        { icon: UserIcon,                  color: 'text-indigo-600 bg-indigo-50', label: 'Updated a user' },
  user_deleted:        { icon: UserIcon,                  color: 'text-red-600 bg-red-50',       label: 'Deleted a user' },
  user_password_reset: { icon: KeyIcon,                   color: 'text-amber-600 bg-amber-50',   label: 'Reset a password' },
};

function metaFor(action) {
  if (ACTION_META[action]) return ACTION_META[action];
  if ((action || '').startsWith('department')) return { icon: BuildingOffice2Icon, color: 'text-cyan-600 bg-cyan-50', label: humanize(action) };
  if ((action || '').startsWith('project')) return { icon: FolderIcon, color: 'text-amber-600 bg-amber-50', label: humanize(action) };
  return { icon: ClockIcon, color: 'text-slate-500 bg-slate-100', label: humanize(action) };
}

const DAYS = [
  { label: 'Today', value: 1 },
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
];

const fmt = (iso) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

function detailsText(raw) {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw);
    const parts = Object.entries(p).map(([k, v]) => `${k}: ${v}`);
    return parts.length ? parts.join(' · ') : null;
  } catch {
    return raw;
  }
}

export default function UserActivityPanel({ user, onClose }) {
  const [days, setDays] = useState(7);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUserActivity(user.id, days);
      setLogs(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [user.id, days]);

  useEffect(() => { load(); }, [load]);

  const close = () => {
    setEntered(false);
    setTimeout(onClose, 200);
  };

  return (
    <div className="fixed inset-0 z-50">
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${entered ? 'opacity-100' : 'opacity-0'}`}
        onClick={close}
      />
      <div
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col transform transition-transform duration-200 ease-out ${
          entered ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{user.full_name}</h3>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
          </div>
          <button onClick={close} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 flex-shrink-0" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Date filter */}
        <div className="px-5 py-3 border-b border-slate-100 flex gap-1">
          {DAYS.map((d) => (
            <button
              key={d.value}
              onClick={() => setDays(d.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                days === d.value ? 'bg-primary-50 text-primary-700' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <ClockIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No activity in this period</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => {
                const meta = metaFor(log.action);
                const Icon = meta.icon;
                const det = detailsText(log.details);
                return (
                  <div key={log.id} className="flex gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800">{meta.label}</p>
                      <p className="text-[11px] text-slate-400">
                        {fmt(log.created_at)}{log.ip_address ? ` · ${log.ip_address}` : ''}
                      </p>
                      {det && <p className="text-xs text-slate-500 mt-0.5 break-words">{det}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
